import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { ArrowUpRight, Box } from "lucide-react";
import { useModels } from "../context/ModelsContext";
import { useAuth } from "../hooks/useAuth";
import * as databaseService from "../services/databaseService";
import XeokitViewer from "./XeokitViewer";
import SignInModal from "./SignInModal";
import DownloadDropdown from "./DownloadDropdown";
import ImageCarousel from "./ImageCarousel";
import "../styles/gallery.css";

function Gallery() {
  const { models: objects, loading } = useModels();
  const { user } = useAuth();
  const { category: urlCategory, productId } = useParams();
  const location = useLocation();
  const searchQuery = location.state?.searchQuery || "";
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tout");
  const [selectedObject, setSelectedObject] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const categories = ["Tout", "Zelige", "Boiserie", "Platre", "Ferronnerie"];

  useEffect(() => {
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (urlCategory) {
      setSelectedCategory(urlCategory);
    }
  }, [urlCategory]);

  useEffect(() => {
    if (productId && objects.length > 0) {
      const obj = objects.find((o) => o.id === productId);
      if (obj) {
        setSelectedObject(obj);
      }
    }
  }, [productId, objects]);

  const handleBackToGallery = () => {
    setSelectedObject(null);
    window.history.replaceState({}, "", "/gallery");
  };

  const filteredObjects = objects.filter((obj) => {
    const matchesSearch =
      obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obj.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "Tout" || 
      obj.category?.toLowerCase() === selectedCategory?.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredObjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedObjects = filteredObjects.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handleDownload = async (obj, format) => {
    // Check if user is authenticated before allowing download
    if (!user) {
      setPendingDownloadFormat(format);
      setShowSignInModal(true);
      return;
    }

    // Get the download URL for the specified format
    let downloadUrl;
    if (format && obj.availableFiles && obj.availableFiles[format]) {
      downloadUrl = obj.availableFiles[format].url;
    }

    if (downloadUrl) {
      try {
        // Track download in model (global counter)
        await databaseService.incrementDownloadCountByFormat(obj.id, format);
        
        // Track download in user record (per-user history)
        await databaseService.trackUserDownload(user.uid, obj.id, format);
        
        window.open(downloadUrl, "_blank");
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    }
  };

  // Handle successful sign-in from modal
  const handleSignInSuccess = () => {
    setShowSignInModal(false);
    setPendingDownloadFormat(null);
  };

  // Watch for user authentication changes to auto-close modal
  useEffect(() => {
    if (user && showSignInModal) {
      setShowSignInModal(false);
      // If there was a pending download, retry it
      if (pendingDownloadFormat && selectedObject) {
        handleDownload(selectedObject, pendingDownloadFormat);
        setPendingDownloadFormat(null);
      }
    }
  }, [user]);

  // Get available formats from model data
  const getAvailableFormats = (obj) => {
    if (!obj.availableFiles) return {};
    return obj.availableFiles;
  };

  // Show loading when we have a productId but haven't loaded the object yet
  if (productId && !selectedObject && loading) {
    return (
      <div className="gallery">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (selectedObject) {
    return (
      <div className="gallery">
        <SignInModal
          isOpen={showSignInModal}
          onClose={() => {
            setShowSignInModal(false);
            setPendingDownloadFormat(null);
          }}
          onSignInSuccess={handleSignInSuccess}
        />
        <div className="product-detail">
          <button className="back-button" onClick={handleBackToGallery}>
            ← Retour à la galerie
          </button>

          <div className="detail-container">
            <div className="detail-viewer">
              {selectedObject.xktFile ? (
                <XeokitViewer
                  modelUrl={selectedObject.xktFile}
                  height="600px"
                  width="100%"
                  enableZoom={true}
                />
              ) : selectedObject.mockupImages && selectedObject.mockupImages.length > 0 ? (
                <ImageCarousel
                  images={selectedObject.mockupImages}
                  alt={selectedObject.name}
                  height="600px"
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "600px",
                    background: "hsl(30, 10%, 20%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                  }}
                >
                  <p style={{ color: "hsl(30, 15%, 60%)" }}>
                    Pas de prévisualisation disponible
                  </p>
                </div>
              )}
            </div>

            <div className="detail-info">
              <h1>{selectedObject.name}</h1>
              <span className="badge">{selectedObject.category}</span>

              <div className="detail-description">
                <h3>Description</h3>
                <p>{selectedObject.description}</p>
              </div>

              <div className="detail-meta">
                <div className="meta-row">
                  <span className="meta-label">Taille du fichier:</span>
                  <span className="meta-value">{selectedObject.fileSize}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Téléchargements:</span>
                  <span className="meta-value">{selectedObject.downloads}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-label">Catégorie:</span>
                  <span className="meta-value">{selectedObject.category}</span>
                </div>
              </div>

              <div className="detail-actions">
                <DownloadDropdown
                  availableFormats={getAvailableFormats(selectedObject)}
                  modelId={selectedObject.id}
                  onDownload={(format) => handleDownload(selectedObject, format)}
                  disabled={!selectedObject.availableFiles || Object.keys(selectedObject.availableFiles).length === 0}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery">
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setPendingDownloadFormat(null);
        }}
        onSignInSuccess={handleSignInSuccess}
      />
      <div className="gallery-header">
        <h1>Moroccan Design BIM</h1>
        <p>Parcourez notre collection de {objects.length} modèles BIM</p>
      </div>

      <div className="gallery-controls">
        <input
          type="text"
          placeholder="Rechercher des modèles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <div className="category-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${
                selectedCategory === cat ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="gallery-grid">
        {paginatedObjects.map((obj, index) => (
          <div
            key={obj.id}
            className="gallery-card group"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => setSelectedObject(obj)}
          >
            <div className="card-image">
              {obj.xktFile ? (
                <XeokitViewer
                  modelUrl={obj.xktFile}
                  height="100%"
                  width="100%"
                  enableZoom={false}
                />
              ) : obj.mockupImages && obj.mockupImages.length > 0 ? (
                <img
                  src={obj.mockupImages[0].url}
                  alt={obj.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : (
                <div className="image-placeholder">
                  <Box className="placeholder-icon" />
                  <span>Pas d'aperçu</span>
                </div>
              )}
              {/* Fallback error display for images */}
              <div className="image-placeholder" style={{ display: "none" }}>
                <Box className="placeholder-icon" />
                <span>Erreur de chargement</span>
              </div>
              <span className="category-badge">{obj.category}</span>
            </div>

            <div className="card-content">
              <h3 className="card-title">{obj.name}</h3>
              <p className="card-description">
                {obj.description ||
                  "Modèle BIM de haute qualité disponible au téléchargement."}
              </p>
              <button className="btn-explore">
                <span>Voir Détails</span>
                <ArrowUpRight className="btn-icon" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredObjects.length === 0 && (
        <div className="no-results">
          <p>Aucun modèle trouvé correspondant à vos critères</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ← Précédent
          </button>
          <div className="pagination-info">
            Page {currentPage} sur {totalPages} ({filteredObjects.length} modèles)
          </div>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}

export default Gallery;
